import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Pagination,
  TableCell,
  TableFooter,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import { getAllDiscountCodes } from "../../../../redux/reducers/clinicSlice";
import { getBillingRevenueReportData } from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import {
  getPetShopRecordBasedType,
  getPharmacyRecordBasedType,
  getTaxAndSubTotalAndTotal,
  getTreatmentRecordBasedType,
  roundWithSignedDiff,
} from "../../../../util/function";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";

const tableHdrs = [
  "invoiceNumber",
  "invoiceDate",
  "totalDue",
  "status",
  "amtPaid",
  "balanceDue",
  "discount",
  "discountCode",
  "taxes",
  "customerUserName",
  "petName",
  "vetName",
  "paymentMode",
  "type",
  "appointmentType",
];

const invoiceMap = [
  { key: "invoiceNumber", idType: "pharmacyDiscountId" },
  { key: "treatmentInvoiceNumber", idType: "treatmentDiscountId" },
  { key: "petShopInvoiceNumber", idType: "petShopDiscountId" },
];

const BillingInvoicesTableReport = () => {
  const dispatch = useDispatch();
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const { billingRevenue } = useSelector((state) => state?.report);
  const { discountCodes } = useSelector((state) => state?.clinic);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getAllDiscountCodes());
    dispatch(getBillingRevenueReportData(""));
  }, [dispatch]);

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const getAllList = () => {
    const reqList = (billingRevenue?.data ?? [])?.flatMap((item) =>
      invoiceMap
        ?.filter(({ key }) => item[key]?.length > 0)
        ?.map(({ key, idType }) => {
          const billList =
            key === "treatmentInvoiceNumber"
              ? getTreatmentRecordBasedType(item?.paymentDetails)
              : key === "invoiceNumber"
                ? getPharmacyRecordBasedType(item?.paymentDetails)
                : key === "petShopInvoiceNumber"
                  ? getPetShopRecordBasedType(item?.paymentDetails)
                  : [];
          return {
            billList,
            invoiceNumber: item?.[key] ?? "",
            usedDicountCode: item?.[idType] ?? [],
            invoiceDate: item?.updatedDate ?? item?.createdDate,
            status: item?.status,
            amtPaid: item?.amtPaid,
            balanceDue: item?.balanceDue,
            customerUserName: item?.userName,
            petName: item?.petName,
            vetName: item?.doctorName,
            paymentMode: item?.paymentMode,
            type: item?.type,
            appointmentType:
              item?.appointmentType === "Home"
                ? "Home Visit"
                : item?.appointmentType,
          };
        }),
    );
    return reqList;
  };

  const totalPage = Math.ceil((getAllList()?.length ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => {
    setPage(selectedPage);
  };

  const getTableData = () => {
    return (getAllList() ?? [])
      ?.slice((page - 1) * rowsPerPage, page * rowsPerPage)
      ?.map((li) => {
        const reqDiscountCodes = (li?.usedDicountCode ?? [])?.map((nl) => {
          const reqCode = discountCodes?.find(
            (dc) => (dc?.billId ?? "")?.toString() === nl,
          );
          if (reqCode) return reqCode?.discountCode ?? "";
          return "";
        });
        const amtDetail = getTaxAndSubTotalAndTotal(li?.billList);
        const totalDue = Number(
          roundWithSignedDiff(amtDetail?.subTotal ?? 0)?.rounded,
        )?.toFixed(2);
        const discount = Number(
          roundWithSignedDiff(amtDetail?.discount ?? 0)?.rounded,
        )?.toFixed(2);
        const taxes = Number(
          roundWithSignedDiff(amtDetail?.tax ?? 0)?.rounded,
        )?.toFixed(2);
        return {
          ...li,
          totalDue,
          discount,
          taxes,
          discountCode:
            reqDiscountCodes?.length > 0 ? reqDiscountCodes?.join(", ") : null,
          invoiceDate: moment(new Date(li?.invoiceDate)).format("DD MMM YYYY"),
          amtPaid: Number(li?.amtPaid ?? 0)?.toFixed(2),
          balanceDue: Number(li?.balanceDue)?.toFixed(2),
        };
      });
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  return (
    <>
      <div className="flex-row-ali-cen mt5 mh20">
        <Typography className="gray15 fs16 font-bold">
          Total {getAllList()?.length ?? 0}
        </Typography>
        <div
          className="flex-row-ali-cen ml50 cursor"
          onClick={handleEditColumn}
        >
          <EditOutlinedIcon
            sx={{
              color: AppColors.appPrimary,
              width: 20,
              height: 20,
            }}
          />
          <Typography className="blue-color fs16 font-medium ml5">
            Edit columns ({crntTblHeders?.length})
          </Typography>
        </div>
        <div className="flex-row-ali-cen ml50 cursor" onClick={handleOpenChart}>
          <AutoGraphIcon
            sx={{
              color: AppColors.appPrimary,
              width: 20,
              height: 20,
            }}
          />
        </div>
        {/* <Typography
          className="red-color fs16 font-medium cursor ml20"
          onClick={handleResetFilter}
        >
          Reset
        </Typography> */}
      </div>
      <div>
        <ReportTable datas={getTableData()} columns={crntTblHeders} />
        <div className="flex1-end">
          {Math.ceil((getAllList()?.length ?? 0) / rowsPerPage) > 1 ? (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={12}>
                  <div className="flex1-end">
                    <Pagination
                      count={totalPage}
                      variant="outlined"
                      color="primary"
                      page={page}
                      onChange={handleChangePage}
                    />
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          ) : null}
        </div>
      </div>

      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <div>
              <CustomButton
                text="Save"
                smallBtn
                onClick={handleSaveTableColumn}
              />
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default BillingInvoicesTableReport;
